import { Button, Popup, Table } from '@approvalmax/ui/src/components';
import { BodyCellProps } from '@approvalmax/ui/src/components/Table/components';
import { useQueryClient } from '@tanstack/react-query';
import { domain } from 'modules/data';
import { useCurrentUser } from 'modules/utils';
import { memo, useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { EQueryKeys } from '../../../../../config';
import type { UserRow } from '../../UserList.types';
import { PermissionsPopupContent } from '../PermissionsPopupContent/PermissionsPopupContent';
import { messages } from './PermissionsCell.messages';

export const PermissionsCell = memo<BodyCellProps<UserRow>>((props) => {
    const { item } = props;

    const [isPopupOpen, toggleIsPopupOpen] = useToggle(false);

    const company = useCompanyBasedOnUrl();

    const currentUser = useCurrentUser();

    const queryClient = useQueryClient();

    const permissionsCount = useMemo(() => {
        const userPermissions = company.userPermissionsSettings?.[item.databaseId ?? ''];

        return userPermissions?.filter((permission) => permission.isEnabled).length ?? 0;
    }, [company.userPermissionsSettings, item.databaseId]);

    const onPermissionsChanged = useCallback(() => {
        queryClient.invalidateQueries([EQueryKeys.Users, company.id]);

        toggleIsPopupOpen(false);
    }, [queryClient, company.id, toggleIsPopupOpen]);

    const isActive =
        (company.author === currentUser.id || company.myRole === domain.CompanyUserRole.Manager) && !company.isReadonly;

    return (
        <Table.Cell {...props}>
            <Button
                noUppercase
                fontWeight='regular'
                size='small'
                variant='text'
                color='blue100'
                disabled={!isActive}
                onClick={toggleIsPopupOpen}
            >
                {permissionsCount === 1
                    ? messages.onePermission
                    : messages.permissionsCount({ count: permissionsCount })}
            </Button>

            <Popup open={isPopupOpen} onToggle={toggleIsPopupOpen}>
                <PermissionsPopupContent user={item} onPermissionsChanged={onPermissionsChanged} />
            </Popup>
        </Table.Cell>
    );
});

PermissionsCell.displayName = 'PermissionsCell';
